import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { useCreateTradeInMutation } from '../../../store/api/tradeInApi';
import { useGetCustomersQuery } from '../../../store/api/salesApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { CustomerFormContent } from '../../sales/pages/CustomerForm';

export default function TradeInForm() {
  const navigate = useNavigate();
  const [createTradeIn, { isLoading, error }] = useCreateTradeInMutation();
  const { data: customersData, isLoading: loadingCustomers, refetch: refetchCustomers } = useGetCustomersQuery({ limit: 100 });
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';

  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);

  const [formData, setFormData] = useState({
    customerId: '',
    deviceModel: '',
    deviceImei: '',
    deviceCondition: '',
    accessoriesIncluded: '',
    estimatedValue: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = await createTradeIn({
        ...formData,
        estimatedValue: parseFloat(formData.estimatedValue) || 0,
      }).unwrap();
      navigate(`/trade-ins/${result.id}`);
    } catch (err) {
      console.error('Failed to create trade-in:', err);
    }
  };
  
  const customers = customersData || [];

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1">
          New Trade-In Intake
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/trade-ins')}>
          Cancel
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error.data?.message || 'Failed to create trade-in ticket'}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={{xs:12}}>
              <Typography variant="h6" gutterBottom>Customer Information</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Autocomplete
                  options={customers}
                  getOptionLabel={(option) => `${option.name} ${option.phone ? `(${option.phone})` : ''}`}
                  value={customers.find(c => c.id === formData.customerId) || null}
                  onChange={(e, newValue) => setFormData(prev => ({ ...prev, customerId: newValue?.id || '' }))}
                  sx={{ flexGrow: 1 }}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Customer" required={!formData.customerId} />
                  )}
                  loading={loadingCustomers}
                />
                <Button variant="outlined" onClick={() => setCustomerDialogOpen(true)}>
                  + Add Customer
                </Button>
              </Box>
            </Grid>

            <Grid size={{xs:12}}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Device Information</Typography>
            </Grid>

            <Grid size={{xs:12,md:6}}>
              <TextField
                fullWidth
                label="Device Model"
                name="deviceModel"
                value={formData.deviceModel}
                onChange={handleChange}
                required
                placeholder="e.g. iPhone 13 Pro 128GB"
              />
            </Grid>
            <Grid size={{xs:12,md:6}}>
              <TextField
                fullWidth
                label="IMEI / Serial Number"
                name="deviceImei"
                value={formData.deviceImei}
                onChange={handleChange}
                placeholder="Enter IMEI or Serial"
              />
            </Grid>
            
            <Grid size={{xs:12,md:6}}>
              <TextField
                fullWidth
                label="Device Condition"
                name="deviceCondition"
                value={formData.deviceCondition}
                onChange={handleChange}
                placeholder="e.g. Good, Minor Scratches, Broken Screen"
              />
            </Grid>

            <Grid size={{xs:12,md:6}}>
              <TextField
                fullWidth
                label={`Estimated Trade-In Value (${currency})`}
                name="estimatedValue"
                type="number"
                value={formData.estimatedValue}
                onChange={handleChange}
                InputProps={{ inputProps: { min: 0, step: '0.01' } }}
              />
            </Grid>

            <Grid size={{xs:12}}>
              <TextField
                fullWidth
                label="Accessories Included"
                name="accessoriesIncluded"
                value={formData.accessoriesIncluded}
                onChange={handleChange}
                multiline
                rows={2}
                placeholder="e.g. Original Box, Charger, Case"
              />
            </Grid>

            <Grid size={{xs:12}}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating...' : 'Create Intake Ticket'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Add Customer Dialog */}
      <Dialog open={customerDialogOpen} onClose={() => setCustomerDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Customer</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <CustomerFormContent 
              onSuccess={(newCustomer) => {
                setCustomerDialogOpen(false);
                refetchCustomers();
                setFormData(prev => ({ ...prev, customerId: newCustomer.id }));
              }} 
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
